/**
 * The attribute chips under a card-database search result: subset · variation ·
 * attributes (RC, Holo, …).
 *
 * The API calls the attributes **`flags`**, and sends them as an ARRAY
 * (`["RC"]`) or `null` — the rows were reading `result.attributes`, which the
 * search payload never contains, so the chips almost never appeared and two
 * printings of the same card looked identical in the list. `subset` likewise
 * arrives nested on the set, not at the top level.
 *
 * The cleanup is not cosmetic fussiness: `variation` comes through as
 * `": Two White Slashes on Front"` and `note` as `"Single Cut Off Post; "` —
 * fragments of a sentence the API assembles elsewhere. Trimmed, they read as
 * labels; untrimmed they read as a bug.
 */
export const matchAttributeChips = (result = {}) => {
  const toList = (value) => {
    if (Array.isArray(value)) return value
    return value ? [value] : []
  }

  return [
    result.subset ?? result.set?.subset,
    result.variation,
    ...toList(result.flags),
    ...toList(result.attributes),
  ]
    .map(value => String(value ?? '').replace(/^[\s:;-]+/, '').replace(/[\s:;-]+$/, ''))
    .filter(Boolean)
    .filter((value, index, list) => list.indexOf(value) === index)
}

export const updateSearchTermsForUnmatchedCard = (card) => {
  let q = []
  if (card?.set) {
    q.push(card.set)
  } else if (card?.brand) {
    q.push(card.brand)
  }

  if (card?.subset) {
    q.push(card.subset)
  }

  if (card?.card_number) {
    q.push(`#${card.card_number}`)
  }

  if (card?.player) {
    q.push(card.player)
  }

  if (card?.attributes) {
    q.push(card.attributes)
  }

  return {
    searchTermForUnmatchedCard_: q.join(' '),
    searchTerms_: q.join(' ')
  }
}

export const getSearchTermForUnmatchedCard = (selectedCardData, listCard) => {
  if ((!selectedCardData?.matches || !selectedCardData.matches.length) && selectedCardData?.llm?.search_suggestion) {
    return selectedCardData.llm.search_suggestion
  }
  return updateSearchTermsForUnmatchedCard(listCard).searchTerms_
}

/**
 * The label for one database-search result: `[card number] [player] [set]`.
 *
 * Built by joining only the parts that EXIST. The rows used to hard-code the
 * `#` and the spacing, so an entry missing its number rendered a bare "#" and
 * an entry missing everything rendered a blank row — which is what made sparse
 * results look like broken results rather than thin ones.
 *
 * Serial number stays appended where present (`#/25`), since it is often the
 * only thing telling two otherwise identical printings apart.
 *
 * Only a positive print run counts as present. Numbered parallels whose run
 * the database doesn't know arrive with `serialNumber` as the STRING "0",
 * which is truthy and printed "#/0" — read as "numbered out of zero". The
 * `SN` chip already says the card is numbered, so dropping the suffix loses
 * nothing true.
 */
export const matchOptionLabel = (result = {}) => {
  const number = result.number ? `#${result.number}` : ''
  const player = result.name || result.player?.name || ''
  const set = result.set?.name || ''
  // parseInt, not Number: a value with trailing text still leads with its run.
  const serial = parseInt(String(result.serialNumber ?? ''), 10) > 0 ? `#/${result.serialNumber}` : ''

  return [number, player, set, serial].filter(Boolean).join(' ')
}

export const getLockSetQueryKey = (batchStore) => {
  let lockSetQueryKey = 'baseSetIds'

  if (batchStore.selectedBatch?.lock_id && batchStore.selectedBatch?.lock_includes_parallels) {
    lockSetQueryKey = 'baseSetIds'
  } else if (batchStore.selectedBatch?.lock_id && !batchStore.selectedBatch?.lock_includes_parallels) {
    lockSetQueryKey = 'setIds'
  }

  return lockSetQueryKey
}

/**
 * Build the options object for `searchStore.searchDatabaseMatches`. Single
 * source shared by the desktop DatabaseMatchField and the mobile search modal
 * so the query contract can never drift between the two surfaces.
 *
 * @param {string} query — the raw search term.
 * @param {Object} batchStore — carries `selectedBatch.type` + `selectedBatch.locked_sets`.
 * @param {Object} [extra] — extra keys to merge (e.g. `{ page }` for load-more).
 */
export const buildDatabaseSearchOptions = (query, batchStore, extra = {}) => {
  const options = { query, kind: batchStore?.selectedBatch?.type, ...extra }
  const params = getParamsBasedOnLockedSets(batchStore?.selectedBatch?.locked_sets)
  if (params.baseSetIds) options.baseSetIds = params.baseSetIds
  if (params.setIds) options.setIds = params.setIds
  return options
}

/**
 * Whether `value` (a card/database match) is blocked from being added to `batch`
 * because the batch is set-locked and the card's set isn't in the lock.
 * Pure — shared by the global search row + its action button.
 *
 * @param {Object} batch — `batchStore.selectedBatch`.
 * @param {Object} value — the database match (needs `value.set`).
 */
export const isBatchLockedForSet = (batch, value) => {
  if (!batch || !value?.set) return false

  const setId = value.set?.parent ? value.set.parent.id : value.set?.id

  // Batch has lock_id and no locked_sets → blocked unless lock_id matches the set
  if (batch.lock_id && !batch.locked_sets?.length) {
    return batch.lock_id !== setId
  }

  // Batch has no lock_id but has locked_sets → blocked unless set is in locked_sets
  if (!batch.lock_id && batch.locked_sets?.length) {
    return !batch.locked_sets.find(item => item.collx_set_id === setId)
  }

  return false
}

export const getParamsBasedOnLockedSets = (lockedSets = []) => {
  let baseSetIds = ''
  let setIds = ''

  lockedSets?.forEach((item) => {
    if (item.include_parallels) {
      if (baseSetIds) {
        baseSetIds = `${baseSetIds},${item.collx_set_id}`
      } else {
        baseSetIds = `${item.collx_set_id}`
      }
    } else {
      if (setIds) {
        setIds = `${setIds},${item.collx_set_id}`
      } else {
        setIds = `${item.collx_set_id}`
      }
    }
  })

  const params = {}

  if (baseSetIds) {
    params['baseSetIds'] = baseSetIds
  } 

  if (setIds) {
    params['setIds'] = setIds
  }

  return params
}